import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/vilaxii";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const vilaxii: CaseStudy = {
  slug: "vilaxii",
  title: "Vilaxii",
  cardTitle: "Разработка сайта производителя современной мебели Vilaxii",
  subtitle: "Каталог и производство дизайнерской мебели",
  industry: { label: "Современная мебель", slug: "furniture" },
  eyebrow: "Корпоративный сайт · Современная мебель",
  h1: "Разработка сайта мебельного бренда Vilaxii",
  lead:
    "Разработали сайт VILAXII — производителя дизайнерской мебели для дома и бизнеса: каталог по сериям, карточки товара, портфолио проектов и раздел «Бизнесу».",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить сайт мебельного бренда" },

  seo: {
    primaryKeyword: "разработка сайта производителя современной мебели",
    secondaryKeywords: [
      "сайт мебельного бренда",
      "разработка сайта мебельной компании",
      "интернет-каталог мебели",
      "сайт производителя дизайнерской мебели",
    ],
    semanticEntities: [
      "каталог мебели",
      "серии коллекций",
      "карточка товара",
      "портфолио проектов",
      "раздел «Бизнесу»",
      "корзина",
    ],
    excludedKeywords: [
      "купить мебель",
      "диван цена",
      "стол купить",
      "современная мебель Москва",
    ],
    title: "Сайт Vilaxii — кейс HAY.agency",
    description:
      "Кейс HAY.agency: сайт бренда современной мебели. Финальную структуру кейса и мокапы собрать после ручной сверки актуальной версии vilaxii.ru.",
    h1: "Разработка сайта мебельного бренда Vilaxii",
    slug: "vilaxii",
    canonicalUrl: `${SITE_URL}/cases/vilaxii`,
    ogTitle: "Разработка сайта мебельного бренда Vilaxii",
    ogDescription:
      "Каталог по сериям, карточка товара, портфолио проектов и раздел «Бизнесу» для производителя дизайнерской мебели.",
    ogImage: `${SITE_URL}${dir}/vilaxii-og.webp`,
    robotsIndex: true,
    datePublished: "2022-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка корпоративных сайтов",
      to: routes.corporate,
      note: "Каталог, карточки и портфолио проектов",
    },
    relatedIndustry: {
      label: "SEO-продвижение интернет-каталогов",
      to: routes.seo,
      note: "Видимость каталога и карточек товара",
    },
    relatedCases: [
      {
        label: "Разработка и продвижение сайта сети клиник Quantum Clinic",
        to: "/cases/quantum-clinic",
        note: "Корпоративный сайт с каталогом услуг",
      },
      {
        label: "Разработка e-commerce платформы для производителя косметики Levrana",
        to: "/cases/levrana",
        note: "Каталог и карточка товара крупного бренда",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен сайт для мебельного бренда?",
    text: "Спроектируем каталог по сериям, карточку товара и портфолио проектов.",
    button: "Обсудить сайт мебельного бренда",
  },

  client: {
    name: "Vilaxii",
    url: "https://vilaxii.ru/",
    city: "Москва",
    industry: "Современная мебель",
  },
  year: "С 2001 года",
  services: [
    "Дизайн сайта",
    "Разработка каталога по сериям",
    "Карточка товара",
    "Портфолио проектов",
    "Раздел «Бизнесу»",
  ],
  platform: "Tilda",
  tags: [
    { label: "Корпоративный сайт", to: routes.corporate },
    { label: "Разработка", to: routes.sites },
  ],
  facts: [
    { label: "Клиент", value: "Vilaxii" },
    { label: "Отрасль", value: "Современная мебель" },
    { label: "Тип проекта", value: "Сайт-каталог производителя мебели" },
    { label: "Платформа", value: "Tilda" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Каталог мебели с фильтрацией по сериям LITE LOFT, X LINE, DETROIT, VINE, MILAN, LE FRANCE, BUSINESS",
      "Карточка товара с названием, серией, габаритами и переходом к покупке",
      "Раздел похожих товаров в карточке",
      "Портфолио реализованных проектов и объектов",
      "Раздел «Бизнесу» для корпоративных заказчиков",
      "Каталог и корзина на платформе Tilda",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "VILAXII производит дизайнерскую мебель для дома и бизнеса с 2001 года. Каталог включает несколько серий мебели, и сайту нужна была понятная навигация по коллекциям и карточка товара, которая доводит посетителя до заказа.",
        "Вторая часть задачи — показать применение мебели через портфолио проектов и закрыть сценарий корпоративных заказчиков разделом «Бизнесу».",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: сдержанная подача мебельного бренда",
    paragraphs: [
      "Интерфейс собран на stone/taupe палитре — интерьерные фотографии и мебель читаются на нейтральном фоне без визуального шума.",
      "Каталог по сериям и карточка товара выстроены так, чтобы посетитель быстро находил подходящую коллекцию и переходил к деталям.",
    ],
    bullets: [
      "Stone/taupe палитра вместо чистого белого",
      "Каталог с фильтром по сериям мебели",
      "Портфолио проектов как доказательство применения",
    ],
  },

  identity: {
    fonts: [
      { name: "Neutralface", role: "Заголовки", sample: "Каталог Vilaxii" },
      { name: "Graphik TT", role: "Интерфейс и описания", sample: "Серия DETROIT" },
    ],
    colors: [
      { hex: "#EDE7DD", name: "Фон витрины" },
      { hex: "#7C6F5E", name: "Акцент бренда" },
      { hex: "#211F1B", name: "Текст" },
    ],
  },

  cover: img(
    "vilaxii-case-cover",
    "Главная страница сайта производителя мебели Vilaxii",
    6,
  ),

  gallery: [
    img("vilaxii-catalog", "Каталог мебели по сериям на сайте Vilaxii", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия повторяет логику десктопа: выбор серии, карточка товара и переход к заказу.",
      "Карточки товара в мобильной версии показаны с корректно загруженными изображениями и ценой.",
    ],
    images: [
      mobile("vilaxii-mobile-hero", "Мобильная главная сайта Vilaxii"),
      mobile("vilaxii-mobile-catalog", "Мобильный каталог мебели Vilaxii"),
    ],
  },

  columnBlocks: [
    {
      id: "catalog",
      title: "Каталог по сериям",
      paragraphs: [
        "Фильтр-табы коллекций LITE LOFT, X LINE, DETROIT, VINE, MILAN, LE FRANCE и BUSINESS позволяют быстро перейти к нужной серии мебели.",
      ],
      bullets: ["Фильтр по сериям", "Карточки коллекций", "Переход в каталог серии"],
    },
    {
      id: "product",
      title: "Карточка мебели",
      paragraphs: [
        "Карточка товара показывает название, серию, габариты, цену и кнопку заказа, а также похожие товары для расширения выбора.",
      ],
      bullets: ["Название и серия", "Габариты и цена", "Похожие товары"],
    },
    {
      id: "projects",
      title: "Проекты",
      paragraphs: [
        "Портфолио интерьеров и объектов показывает мебель в реальном применении и подтверждает опыт производителя.",
      ],
      bullets: ["Портфолио интерьеров", "Реализованные объекты"],
    },
    {
      id: "business",
      title: "Раздел «Бизнесу»",
      paragraphs: [
        "Отдельный раздел закрывает сценарий корпоративных заказчиков мебели для офисов и коммерческих объектов.",
      ],
      bullets: ["Раздел «Бизнесу»", "Сценарий заказа для организаций"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка корпоративного сайта", to: routes.corporate },
      { label: "Каталог и карточка товара", to: routes.sites },
    ],
  },
};
